import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { APPLIANCE_PLAN_PRICING, ALL_INCLUSIVE_PLAN, BOILER_PLANS } from '../mock';

/**
 * Homepage pricing block. Mirrors the exact prices used in the /quote wizard:
 *   - Appliance Service Plans (per appliance count 1–5)
 *   - All Inclusive (up to 7 appliances)
 *   - Boiler Only (annual only)
 *   - Boiler + Plumbing & Drainage (annual only)
 *
 * Compliance: no unverified claims, "not an insurance product" disclosed,
 * Direct Bank Transfer + "call for other options" preserved, no card claims.
 */

const APPLIANCE_INCLUDES = [
  'Mechanical & electrical breakdown support',
  'Parts and labour under plan T&Cs',
  'UK engineer network — Mon–Fri, 9am–5pm customer service',
  'Cancel any time · 14-day cooling-off period',
];

const ALL_INC_INCLUDES = [
  'Cover for up to 7 appliances',
  'Mechanical & electrical breakdown support',
  'Priority booking within engineer availability',
  'Cancel any time · 14-day cooling-off period',
];

const BOILER_ONLY_INCLUDES = [
  'Boiler maintenance & breakdown support',
  'Radiators, pumps, valves & controls (per T&Cs)',
  'Repairs arranged through qualified engineers',
  'Monthly option available — please call to arrange',
];

const BOILER_PLUS_INCLUDES = [
  'Everything in Boiler Only',
  'Plumbing & drainage emergency support',
  'Repairs arranged through qualified engineers',
  'Monthly option available — please call to arrange',
];

const PhoneNote = () => (
  <p className="text-[11px] text-[#1F0068]/60 mt-4 flex items-center gap-1.5 leading-tight">
    <Phone className="w-3 h-3 text-[#FF5C39] shrink-0" />
    Other payment options — call <a href="tel:01135487557" className="whitespace-nowrap font-semibold text-[#1F0068] underline underline-offset-2 decoration-[#FF5C39]/60 hover:decoration-[#FF5C39] ml-0.5">0113&nbsp;548&nbsp;7557</a>.
  </p>
);

const Pricing = () => {
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [appCount, setAppCount] = useState(1);

  const applianceRow = APPLIANCE_PLAN_PRICING.find((p) => p.count === appCount) || APPLIANCE_PLAN_PRICING[0];
  const showBoilerNote = billing === 'monthly';

  return (
    <section className="relative py-20 md:py-28 bg-white overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-[50%] bg-[#FFEDD6] blur-3xl opacity-50" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5">
        {/* Header + toggle */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-[#FFF6EB] border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#FF5C39]" /> Simple pricing, one plan per home
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-[#1F0068] tracking-tight leading-[1.03]">
            Choose the plan that fits <span className="text-[#FF5C39]">your home</span>
          </h2>
          <p className="mt-5 text-lg text-[#1F0068]/70 leading-relaxed">
            Every plan is a maintenance service agreement — not an insurance product. Pay monthly or annually. Direct Bank Transfer, easy setup, cancel any time.
          </p>

          {/* Monthly / Annual toggle */}
          <div className="mt-8 inline-flex items-center gap-1 bg-[#FFF6EB] rounded-full p-1 border border-[#1F0068]/10">
            <button data-testid="pricing-toggle-monthly" onClick={() => setBilling('monthly')} className={`px-5 h-10 rounded-full text-sm font-semibold transition-all ${billing === 'monthly' ? 'bg-[#1F0068] text-white shadow-sm' : 'text-[#1F0068]/70 hover:text-[#1F0068]'}`}>Monthly</button>
            <button data-testid="pricing-toggle-annual" onClick={() => setBilling('annual')} className={`px-5 h-10 rounded-full text-sm font-semibold transition-all ${billing === 'annual' ? 'bg-[#FF5C39] text-white shadow-sm' : 'text-[#1F0068]/70 hover:text-[#1F0068]'}`}>Pay Annually</button>
          </div>
        </div>

        {/* Row 1 — Appliance plans (with count picker) + All Inclusive */}
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Card 1 — Appliance Service Plans */}
          <div data-testid="pricing-card-appliance" className="rounded-[32px] bg-gradient-to-br from-[#FFF6EB] to-[#FFEDD6] border border-[#1F0068]/8 p-8 md:p-10 flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#FF5C39]">Appliance Service Plan</div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#1F0068] mt-1 tracking-tight">Cover 1 to 5 appliances</h3>
              </div>
            </div>
            <p className="text-sm text-[#1F0068]/70 mt-1">Pick how many appliances you'd like to include. Price updates automatically.</p>

            {/* Appliance count segmented control */}
            <div className="mt-6 grid grid-cols-5 gap-1.5 bg-white/70 rounded-full p-1.5 border border-[#1F0068]/10">
              {APPLIANCE_PLAN_PRICING.map((p) => (
                <button
                  key={p.count}
                  data-testid={`pricing-appliance-count-${p.count}`}
                  onClick={() => setAppCount(p.count)}
                  className={`h-10 rounded-full text-sm font-bold transition-all ${appCount === p.count ? 'bg-[#1F0068] text-white shadow-sm' : 'text-[#1F0068]/60 hover:text-[#1F0068]'}`}
                >
                  {p.count}
                </button>
              ))}
            </div>

            <div className="mt-6 flex items-end gap-2">
              <span data-testid="pricing-appliance-price" className="text-6xl font-extrabold text-[#1F0068] leading-none tracking-tight">
                £{billing === 'monthly' ? applianceRow.monthly.toFixed(2) : applianceRow.annual.toFixed(2)}
              </span>
              <span className="text-[#1F0068]/60 font-semibold pb-2">/{billing === 'monthly' ? 'month' : 'year'}</span>
            </div>
            <div className="text-xs text-[#1F0068]/60 mt-1">
              {applianceRow.name} · {billing === 'monthly' ? `or £${applianceRow.annual.toFixed(2)} paid annually` : `equivalent to £${(applianceRow.annual / 12).toFixed(2)}/mo`}
            </div>

            <ul className="mt-6 space-y-2.5 text-sm text-[#1F0068]/85">
              {APPLIANCE_INCLUDES.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00b67a] mt-0.5 shrink-0" /><span>{f}</span></li>
              ))}
            </ul>

            <div className="mt-auto pt-8">
              <Link to="/quote">
                <Button data-testid="pricing-appliance-cta" className="w-full rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white h-12 gap-1.5 font-semibold">
                  {billing === 'monthly' ? 'Choose Monthly' : 'Pay Annually'} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <PhoneNote />
            </div>
          </div>

          {/* Card 2 — All Inclusive (highlighted) */}
          <div data-testid="pricing-card-all-inclusive" className="relative rounded-[32px] bg-[#1F0068] text-white p-8 md:p-10 flex flex-col overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#FF5C39]/20 blur-3xl pointer-events-none" />
            <div className="absolute top-6 right-6 bg-[#FF5C39] text-white text-[10px] uppercase tracking-[0.28em] font-bold px-3 py-1.5 rounded-full">Most cover</div>

            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#FF5C39]">All Inclusive Home Protection</div>
              <h3 className="text-2xl md:text-3xl font-bold mt-1 tracking-tight">Up to 7 appliances</h3>
              <p className="text-sm text-white/80 mt-1 max-w-md">One plan for your whole home — covers up to seven eligible household appliances.</p>

              <div className="mt-6 flex items-end gap-2">
                <span data-testid="pricing-all-inclusive-price" className="text-6xl font-extrabold leading-none tracking-tight">
                  £{billing === 'monthly' ? ALL_INCLUSIVE_PLAN.monthly.toFixed(2) : ALL_INCLUSIVE_PLAN.annual.toFixed(2)}
                </span>
                <span className="text-white/70 font-semibold pb-2">/{billing === 'monthly' ? 'month' : 'year'}</span>
              </div>
              <div className="text-xs text-white/60 mt-1">
                {billing === 'monthly' ? `or £${ALL_INCLUSIVE_PLAN.annual.toFixed(2)} paid annually` : `equivalent to £${(ALL_INCLUSIVE_PLAN.annual / 12).toFixed(2)}/mo`}
              </div>

              <ul className="mt-6 space-y-2.5 text-sm text-white/85">
                {ALL_INC_INCLUDES.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#FF5C39] mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>

              <div className="mt-8">
                <Link to="/quote">
                  <Button data-testid="pricing-all-inclusive-cta" className="w-full rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white h-12 gap-1.5 font-semibold">
                    {billing === 'monthly' ? 'Choose Monthly' : 'Pay Annually'} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <p className="text-[11px] text-white/60 mt-4 flex items-center gap-1.5 leading-tight">
                  <Phone className="w-3 h-3 text-[#FF5C39] shrink-0" />
                  Other payment options — call <a href="tel:01135487557" className="whitespace-nowrap font-semibold text-white underline underline-offset-2 decoration-[#FF5C39]/60 hover:decoration-[#FF5C39] ml-0.5">0113&nbsp;548&nbsp;7557</a>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2 — Boiler plans (annual-only) */}
        <div className="grid lg:grid-cols-2 gap-5 mt-5">
          {BOILER_PLANS.map((b, i) => {
            const includes = i === 0 ? BOILER_ONLY_INCLUDES : BOILER_PLUS_INCLUDES;
            const isFirst = i === 0;
            return (
              <div key={b.id} data-testid={`pricing-card-${b.id}`} className={`rounded-[32px] p-8 md:p-10 flex flex-col ${isFirst ? 'bg-white border border-[#1F0068]/8' : 'bg-gradient-to-br from-[#FCE0EC] to-[#FFEDD6] border border-[#FF5C39]/10'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#FF5C39]">{b.tag}</div>
                    <h3 className="text-2xl md:text-3xl font-bold text-[#1F0068] mt-1 tracking-tight">{b.name}</h3>
                  </div>
                </div>

                <div className="mt-6 flex items-end gap-2">
                  <span data-testid={`pricing-${b.id}-price`} className="text-6xl font-extrabold text-[#1F0068] leading-none tracking-tight">£{b.annual.toFixed(2)}</span>
                  <span className="text-[#1F0068]/60 font-semibold pb-2">/year</span>
                </div>
                <div className="text-xs text-[#1F0068]/60 mt-1">Annual plan · equivalent to £{(b.annual / 12).toFixed(2)}/mo</div>

                {showBoilerNote && (
                  <div className="mt-4 rounded-xl bg-[#FFEDD6] px-4 py-3 text-[12px] text-[#1F0068]/85 flex items-start gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#FF5C39] mt-0.5 shrink-0" />
                    <span>You've picked Monthly — for a monthly payment option on this plan, please call <a href="tel:01135487557" className="whitespace-nowrap font-bold text-[#1F0068] underline underline-offset-2 decoration-[#FF5C39]/60 ml-0.5">0113&nbsp;548&nbsp;7557</a> to arrange.</span>
                  </div>
                )}

                <ul className="mt-6 space-y-2.5 text-sm text-[#1F0068]/85">
                  {includes.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#00b67a] mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  <Link to="/quote">
                    <Button data-testid={`pricing-${b.id}-cta`} className="w-full rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white h-12 gap-1.5 font-semibold">
                      Pay Annually <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <PhoneNote />
                </div>
              </div>
            );
          })}
        </div>

        {/* Fine-print */}
        <p className="mt-8 text-[11px] text-[#1F0068]/55 max-w-4xl leading-relaxed">
          All prices are for maintenance service agreements provided by Domestic Home Services Limited. Repairs and maintenance visits are arranged through our network of independent UK service providers. Cover is provided in accordance with your plan Terms &amp; Conditions. Cancellation admin charge may apply after the 14-day cooling-off period — see our <a href="/legal/cancellation-policy" className="underline hover:text-[#1F0068]">Cancellation Policy</a>.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
